import React, { useEffect, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, Calendar, MapPin, Users, Clock, ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function groupByDate(events) {
  const groups = {};
  for (const ev of events) {
    const label = formatDate(ev.start);
    if (!groups[label]) groups[label] = [];
    groups[label].push(ev);
  }
  return groups;
}

function EventCard({ event, onRsvp }) {
  const isFull = event.rsvp_full && !event.waitlist_enabled;

  return (
    <div
      data-testid={`event-card-${event.id}`}
      className="bg-white rounded-xl border p-4 flex gap-3 cursor-pointer hover:shadow-sm transition-shadow"
      style={{ borderLeftColor: event.color || '#6366f1', borderLeftWidth: 4 }}
      onClick={() => onRsvp(event)}
    >
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">{event.title}</p>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-gray-500">
          {event.all_day ? (
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> All day</span>
          ) : (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />{formatTime(event.start)}{event.end ? ` – ${formatTime(event.end)}` : ''}
            </span>
          )}
          {event.location && (
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</span>
          )}
          {event.spots_left !== null && event.spots_left !== undefined && (
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {event.spots_left === 0 ? 'Full' : `${event.spots_left} spots left`}
            </span>
          )}
        </div>
        {event.category && (
          <Badge variant="secondary" className="mt-1.5 text-xs py-0">{event.category}</Badge>
        )}
      </div>
      {event.rsvp_enabled && (
        <div className="flex-shrink-0 flex items-center">
          <Badge
            variant={isFull ? 'destructive' : 'outline'}
            className="text-xs"
            data-testid={`event-rsvp-badge-${event.id}`}
          >
            {isFull ? 'Full' : event.waitlist_enabled && event.rsvp_full ? 'Waitlist' : 'RSVP'}
          </Badge>
        </div>
      )}
    </div>
  );
}

function RsvpModal({ event, customerId, user, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '', party_size: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.getPublicNexusEventDetail(customerId, event.id)
      .then(d => setDetail(d))
      .catch(() => setDetail(event))
      .finally(() => setLoadingDetail(false));
  }, [event.id, customerId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = { ...form };
      if (!body.party_size) body.party_size = 1;
      const res = await api.submitNexusRsvp(customerId, event.id, body);
      setResult(res);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'RSVP failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const fields = detail?.rsvp_fields || [
    { key: 'name', label: 'Full name', type: 'text', required: true },
    { key: 'email', label: 'Email address', type: 'email', required: true },
  ];

  const statusColors = {
    confirmed: 'text-green-700 bg-green-50 border-green-200',
    waitlisted: 'text-amber-700 bg-amber-50 border-amber-200',
    full: 'text-red-700 bg-red-50 border-red-200',
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="rsvp-modal">
        <DialogHeader>
          <DialogTitle className="text-base">{event.title}</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className={`rounded-lg border p-4 text-sm ${statusColors[result.status] || ''}`}>
            <p className="font-semibold capitalize mb-1">{result.status === 'confirmed' ? 'You\'re registered!' : result.status}</p>
            <p>{result.message}</p>
            {result.event?.location && (
              <p className="mt-2 flex items-center gap-1 text-xs">
                <MapPin className="w-3 h-3" />{result.event.location}
              </p>
            )}
            <Button className="w-full mt-3" variant="outline" onClick={onClose} size="sm">Close</Button>
          </div>
        ) : loadingDetail ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground space-y-1 mb-2">
              {!event.all_day && (
                <p className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />{formatTime(event.start)}{event.end ? ` – ${formatTime(event.end)}` : ''}
                </p>
              )}
              {event.location && <p className="flex items-center gap-1"><MapPin className="w-3 h-3" />{event.location}</p>}
            </div>
            {detail?.description_html && (
              <div
                className="text-xs text-gray-600 mb-3 max-h-24 overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: detail.description_html }}
              />
            )}
            {event.rsvp_enabled ? (
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="rsvp-form">
                {fields.map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label className="text-xs">{f.label}{f.required ? ' *' : ''}</Label>
                    <Input
                      type={f.type || 'text'}
                      required={f.required}
                      value={form[f.key] ?? (f.default ?? '')}
                      min={f.min}
                      onChange={(e) => setForm(prev => ({ ...prev, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value }))}
                      data-testid={`rsvp-field-${f.key}`}
                    />
                  </div>
                ))}
                <Button
                  type="submit"
                  className="w-full"
                  disabled={submitting || (event.rsvp_full && !event.waitlist_enabled)}
                  data-testid="rsvp-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {event.rsvp_full && !event.waitlist_enabled
                    ? 'Event Full'
                    : event.rsvp_full && event.waitlist_enabled
                      ? 'Join Waitlist'
                      : 'Confirm RSVP'}
                </Button>
              </form>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-2">No RSVP required for this event.</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function NexusEventsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const customerId = user?.customer_id;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | week | month
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    const now = new Date().toISOString().slice(0, 10);
    api.getPublicNexusEvents(customerId, { from: now, limit: 100 })
      .then(data => setEvents(data.events || []))
      .catch(() => toast.error('Could not load events'))
      .finally(() => setLoading(false));
  }, [customerId]);

  const filterEvents = (evs) => {
    if (filter === 'all') return evs;
    const now = new Date();
    const cutoff = new Date(now);
    if (filter === 'week') cutoff.setDate(now.getDate() + 7);
    else cutoff.setMonth(now.getMonth() + 1);
    return evs.filter(e => new Date(e.start) <= cutoff);
  };

  const filtered = filterEvents(events);
  const grouped = groupByDate(filtered);

  return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto px-4 py-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-1.5 rounded-lg hover:bg-gray-100"
            data-testid="events-back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Calendar className="w-5 h-5" /> Events
          </h1>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4" data-testid="events-filter-tabs">
          {[['all', 'All'], ['week', 'This Week'], ['month', 'This Month']].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilter(val)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                filter === val ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              data-testid={`events-filter-${val}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground" data-testid="events-empty">
            <Calendar className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No upcoming events</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="events-list">
            {Object.entries(grouped).map(([date, dayEvents]) => (
              <div key={date}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  {date}
                </p>
                <div className="space-y-2">
                  {dayEvents.map(ev => (
                    <EventCard key={ev.id} event={ev} onRsvp={setSelectedEvent} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedEvent && (
        <RsvpModal
          event={selectedEvent}
          customerId={customerId}
          user={user}
          onClose={() => setSelectedEvent(null)}
        />
      )}
    </DashboardLayout>
  );
}
